import { dashboardAppointmentSchema } from '../appointments/appointments-contracts'
import { MAX_APPOINTMENTS_PER_CLINIC_DAY } from '../appointments/appointment-sync'
import { z } from 'zod'
import {
  normalizeAppointment,
  type AppointmentItem,
} from '../appointments/appointments-model'
import { timeZoneSchema } from './overview-contracts'

export type ClinicDayBounds = {
  date: string
  timeZone: string
  startAtMs: number
  endBeforeMs: number
}

export type TodaySchedulePage = {
  valid: boolean
  items: AppointmentItem[]
}

const appointmentCoverageSchema = z
  .object({
    schemaVersion: z.literal(1),
    status: z.literal('COMPLETE'),
    windowStartAtMs: z.number().finite().positive(),
    windowEndBeforeMs: z.number().finite().positive(),
    clinicTimeZone: timeZoneSchema,
    appointmentCount: z
      .number()
      .int()
      .min(0)
      .max(MAX_APPOINTMENTS_PER_CLINIC_DAY),
    observedAtMs: z.number().finite().positive(),
    coverageFingerprint: z.string().regex(/^[a-f0-9]{64}$/),
  })
  .strict()

export function hasCompleteTodayCoverage(
  value: unknown,
  bounds: ClinicDayBounds,
  visibleAppointmentCount: number
) {
  const parsed = appointmentCoverageSchema.safeParse(value)
  return (
    parsed.success &&
    parsed.data.windowStartAtMs === bounds.startAtMs &&
    parsed.data.windowEndBeforeMs === bounds.endBeforeMs &&
    parsed.data.clinicTimeZone === bounds.timeZone &&
    parsed.data.appointmentCount === visibleAppointmentCount &&
    parsed.data.observedAtMs >= bounds.startAtMs &&
    parsed.data.observedAtMs < bounds.endBeforeMs
  )
}

function calendarParts(timestamp: number, timeZone: string) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(timestamp)
  const read = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value)
  return {
    year: read('year'),
    month: read('month'),
    day: read('day'),
    hour: read('hour'),
    minute: read('minute'),
    second: read('second'),
  }
}

function localMidnightEpoch(
  date: { year: number; month: number; day: number },
  timeZone: string
) {
  const target = Date.UTC(date.year, date.month - 1, date.day)
  let candidate = target
  for (let attempt = 0; attempt < 4; attempt += 1) {
    const rendered = calendarParts(candidate, timeZone)
    const renderedAsUtc = Date.UTC(
      rendered.year,
      rendered.month - 1,
      rendered.day,
      rendered.hour,
      rendered.minute,
      rendered.second
    )
    candidate += target - renderedAsUtc
  }
  const verified = calendarParts(candidate, timeZone)
  if (
    verified.year !== date.year ||
    verified.month !== date.month ||
    verified.day !== date.day ||
    verified.hour !== 0 ||
    verified.minute !== 0
  ) {
    return null
  }
  return candidate
}

export function clinicTodayBounds(
  value: unknown,
  nowMs = Date.now()
): ClinicDayBounds | null {
  const parsed = timeZoneSchema.safeParse(value)
  if (!parsed.success || !Number.isFinite(nowMs)) return null
  const timeZone = parsed.data
  const today = calendarParts(nowMs, timeZone)
  const nextDate = new Date(
    Date.UTC(today.year, today.month - 1, today.day + 1)
  )
  const startAtMs = localMidnightEpoch(today, timeZone)
  const endBeforeMs = localMidnightEpoch(
    {
      year: nextDate.getUTCFullYear(),
      month: nextDate.getUTCMonth() + 1,
      day: nextDate.getUTCDate(),
    },
    timeZone
  )
  if (startAtMs === null || endBeforeMs === null || endBeforeMs <= startAtMs) {
    return null
  }
  return {
    date: `${String(today.year).padStart(4, '0')}-${String(today.month).padStart(2, '0')}-${String(today.day).padStart(2, '0')}`,
    timeZone,
    startAtMs,
    endBeforeMs,
  }
}

export function parseTodaySchedule(
  value: unknown,
  bounds: ClinicDayBounds
): TodaySchedulePage {
  if (value === null) return { valid: true, items: [] }
  if (typeof value !== 'object' || Array.isArray(value)) {
    return { valid: false, items: [] }
  }
  const entries = Object.entries(value as Record<string, unknown>)
  if (entries.length > MAX_APPOINTMENTS_PER_CLINIC_DAY) {
    return { valid: false, items: [] }
  }
  const items: AppointmentItem[] = []
  for (const [appointmentId, record] of entries) {
    const parsed = dashboardAppointmentSchema.safeParse(record)
    if (
      !parsed.success ||
      parsed.data.appointmentId !== appointmentId ||
      parsed.data.temporalConfidence !== 'canonical' ||
      parsed.data.scheduledAtMs === null ||
      parsed.data.scheduledAtMs < bounds.startAtMs ||
      parsed.data.scheduledAtMs >= bounds.endBeforeMs ||
      parsed.data.clinicTimeZone !== bounds.timeZone
    ) {
      return { valid: false, items: [] }
    }
    items.push(normalizeAppointment(parsed.data))
  }
  items.sort(
    (left, right) =>
      (left.scheduledAtMs ?? 0) - (right.scheduledAtMs ?? 0) ||
      left.appointmentId.localeCompare(right.appointmentId)
  )
  return { valid: true, items }
}
